import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { GoogleLogin } from '@react-oauth/google';
import { authApi } from '@/features/auth/api/auth.api';
import type { AuthResponse } from '@/types';
import { Button, PhoneInput } from '@/shared/components/ui';
import { indianMobileSchemaMessage, toE164India } from '@/shared/utils/phone';
import { legalConfig, type SmsConsentSource } from '@/shared/config/legal';
import { SmsConsentField } from '@/features/legal/components/SmsConsentField';
import { GOOGLE_ACCOUNT_PHONE_MESSAGE } from '../constants';

const schema = z.object({
  phone: z
    .string()
    .trim()
    .regex(/^\d{10}$/, indianMobileSchemaMessage),
  smsConsent: z.boolean().refine((value) => value === true, {
    message: 'Please agree to receive verification SMS to continue.',
  }),
});

type FormData = z.infer<typeof schema>;

interface PhoneAuthFormProps {
  title?: string;
  subtitle?: string;
  onOtpSent: (phone: string) => void;
  onGoogleSuccess: (data: AuthResponse) => void;
  intent?: string;
  from?: string;
  travelerSearch?: boolean;
  /** Where this form is rendered — stored with SMS consent records. */
  smsConsentSource?: SmsConsentSource;
  /** Compact spacing for dialog surfaces. */
  compact?: boolean;
}

export function PhoneAuthForm({
  title,
  subtitle,
  onOtpSent,
  onGoogleSuccess,
  intent,
  from,
  travelerSearch,
  smsConsentSource = 'web_phone_auth',
  compact = false,
}: PhoneAuthFormProps) {
  const [error, setError] = useState('');
  const googleContainerRef = useRef<HTMLDivElement>(null);
  const [googleButtonWidth, setGoogleButtonWidth] = useState(320);

  useEffect(() => {
    const node = googleContainerRef.current;
    if (!node) return;
    const updateWidth = () => {
      const width = Math.floor(node.getBoundingClientRect().width);
      if (width > 0) setGoogleButtonWidth(width);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      phone: '',
      smsConsent: false,
    },
  });

  const phone = watch('phone');
  const smsConsent = watch('smsConsent');
  const canSendOtp = /^\d{10}$/.test(phone?.trim() ?? '') && smsConsent === true;

  const sendOtpMutation = useMutation({
    mutationFn: (phone: string) =>
      authApi.sendOtp(phone, {
        smsConsent: true,
        smsConsentVersion: legalConfig.smsConsentVersion,
        smsConsentSource,
      }),
    onSuccess: (_, phone) => onOtpSent(phone),
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      const serverMessage = axiosErr.response?.data?.message?.trim();
      setError(serverMessage || 'Failed to send OTP. Please try again.');
    },
  });

  const googleMutation = useMutation({
    mutationFn: (idToken: string) =>
      authApi.googleAuth(idToken, { intent, from, travelerSearch }),
    onSuccess: (data) => onGoogleSuccess(data),
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      const serverMessage = axiosErr.response?.data?.message?.trim();
      setError(serverMessage || 'Google Sign-In failed. Please try again.');
    },
  });

  const onSubmit = (data: FormData) => {
    setError('');
    sendOtpMutation.mutate(toE164India(data.phone));
  };

  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography
          variant="h5"
          fontWeight={700}
          textAlign="center"
          sx={{
            mb: subtitle ? 0.75 : compact ? 2 : 2.5,
            fontSize: compact ? { xs: '1.25rem', sm: '1.375rem' } : undefined,
            letterSpacing: '-0.02em',
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{
            mb: compact ? 2.25 : 2.5,
            lineHeight: 1.6,
            maxWidth: compact ? 340 : 320,
            mx: 'auto',
            fontSize: compact ? '0.875rem' : undefined,
          }}
        >
          {subtitle}
        </Typography>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2, py: 0.75 }}
          onClose={() => setError('')}
        >
          {error}
          {(error === GOOGLE_ACCOUNT_PHONE_MESSAGE || error.includes('Google Sign-In')) && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              Use the Google button below to continue.
            </Typography>
          )}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <PhoneInput
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              fullWidth
              label="Mobile number"
              error={!!errors.phone}
              helperText={errors.phone?.message || 'Enter your 10-digit number'}
              sx={{ mb: compact ? 1.25 : 1.5 }}
            />
          )}
        />

        <SmsConsentField control={control} name="smsConsent" compact={compact} />

        <Button
          type="submit"
          intent="primary-dark"
          fullWidth
          size="large"
          disableMotion
          disabled={!canSendOtp || sendOtpMutation.isPending}
          sx={{ py: compact ? 1.2 : 1.35, mb: compact ? 2 : 2.5, borderRadius: 99 }}
        >
          {sendOtpMutation.isPending ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            'Send OTP'
          )}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: compact ? 2 : 2.5 }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          sx={{ letterSpacing: '0.08em', px: 0.5 }}
        >
          OR
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/*
        Guide entry is phone-first by design.

        An applicant gives us a phone and an email. Phone OTP always finds
        their application; Google only finds it when the Google address
        happens to match the one they applied with. Signing in with a
        different Gmail creates a separate account and leaves the application
        stranded on the first one — the exact defect this flow exists to
        prevent. Google stays available, because plenty of applicants DO use
        their Gmail, but it is no longer the path of least resistance.
      */}
      {intent === 'guide' && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Applying to be a Guide? Use the mobile number from your application —
          it is the surest way to reach it. Google works too, but only if you
          pick the same email address you applied with.
        </Alert>
      )}

      <Box
        ref={googleContainerRef}
        sx={{
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'stretch',
          borderRadius: 2,
          '& > div': { width: '100% !important', maxWidth: '100% !important' },
          '& iframe': { width: '100% !important', maxWidth: '100% !important' },
        }}
      >
        {googleMutation.isPending ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              py: 1.5,
              width: '100%',
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Signing in with Google...
            </Typography>
          </Box>
        ) : (
          <GoogleLogin
            onSuccess={(cred) => {
              if (cred.credential) {
                setError('');
                googleMutation.mutate(cred.credential);
              }
            }}
            onError={() => setError('Google Sign-In failed. Please try again.')}
            width={String(googleButtonWidth)}
            text="continue_with"
            shape="rectangular"
            size="large"
          />
        )}
      </Box>
    </Box>
  );
}
